import { useLocation, useSearchParams } from 'react-router';
import type { FeatureFlags } from '@modules/root';
import { hasFeature, useFeatureFlags } from '@modules/root';
import { useAppData } from '~/modules/root/hook/use-app-data';
import type { User } from '~/server/types';

/**
 * Whether this reader is ArchiPro staff.
 *
 * Staff-only is deliberate. The button exists so the team can report on the revamp while it is in
 * beta, and Mark's call in the launch thread was to keep it internal — opening it to clients is a
 * separate decision that goes through Milot.
 */
export const isSiteFeedbackAudience = (
    user: User | null | undefined,
): boolean => user?.__typename === 'Me' && user.IsArchiproStaff === true;

/**
 * Business Manager, where the button has no business being.
 *
 * A path test rather than a route `handle`, which is how the rest of the global
 * chrome is suppressed here. The rule is not "this route opts out" — it is that
 * the button reports on the public site and Business Manager is a different
 * product. Written as an opt-out it would have to be repeated in each of the
 * business routes' handle blocks, and there is no parent layout route to hang
 * one on: the next BM page migrated to Remix would arrive with the button back.
 */
const isBusinessManagerPath = (pathname: string): boolean =>
    pathname === '/business' || pathname.startsWith('/business/');

/**
 * The complete gate for the sitewide feedback button: staff, on the revamp, and
 * off Business Manager.
 *
 * The revamp flag and the staff check are each load-bearing and neither is redundant. The flag
 * scopes the button to the pages under review; the staff check is what keeps it off the public site
 * when that flag goes out to everyone. Dropping either one ships a feedback button to the whole
 * internet. The path test is narrower: staff on the revamp are exactly who is in Business Manager,
 * so the other two would both pass there.
 *
 * `?revamp=1` stands in for the flag outside production, the same escape hatch the rest of the
 * revamp uses so these pages can be QA'd before the flag reaches a team server. It cannot widen
 * the audience: it is compiled out of production, and the staff check still has to pass.
 */
const isGateOn = (
    user: User | null | undefined,
    featureFlags: FeatureFlags,
    searchParams: URLSearchParams,
    pathname: string,
): boolean => {
    if (!isSiteFeedbackAudience(user)) return false;
    if (isBusinessManagerPath(pathname)) return false;

    return (
        hasFeature(featureFlags, 'revamp_2026') ||
        (process.env.NODE_ENV !== 'production' &&
            searchParams.get('revamp') === '1')
    );
};

/** The gate for the mount site, from app data and the current location. */
export const useSiteFeedbackEnabled = (): boolean => {
    const { user } = useAppData();
    const featureFlags = useFeatureFlags();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    return isGateOn(user, featureFlags, searchParams, pathname);
};

/** The same gate, for tests and any caller that already holds the inputs. */
export const isSiteFeedbackEnabled = isGateOn;
